import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContactRound, Pencil, X as CloseIcon } from "lucide-react";
import type { ContactDetail, ContactGroupSummary, UpdateContactRequest } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../../../components/ui/sheet/index.js";
import {
  CONTACT_CRM_SHEET_CLASS,
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetHeaderIconClass,
  contactSheetTitleClass,
} from "../contact-sheet-presentation.js";
import {
  CONTACT_SHEET_MOTION_CLASS,
  CONTACT_SHEET_OVERLAY_MOTION_CLASS,
} from "../contacts-table-presentation.js";
import { cn } from "../../../lib/utils.js";
import { ContactDetailEditor, type ContactDetailMode } from "./ContactDetailEditor.js";

export function ContactDetailSheet({
  open,
  detail,
  loading,
  groups,
  organizationId,
  onClose,
  onSave,
  onDelete,
  onRestore,
  onMerged,
}: {
  open: boolean;
  detail: ContactDetail | null;
  loading: boolean;
  groups: ContactGroupSummary[];
  organizationId: number;
  onClose: () => void;
  onSave: (body: UpdateContactRequest) => Promise<void>;
  onDelete: () => Promise<void>;
  onRestore: () => Promise<void>;
  onMerged?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const [mode, setMode] = useState<ContactDetailMode>("view");

  useEffect(() => {
    if (!open) return;
    setMode("view");
  }, [open, detail?.id]);

  const title = mode === "edit" ? t("contacts.detail.editTitle") : t("contacts.detail.title");
  const description =
    mode === "edit" ? t("contacts.detail.editDescription") : t("contacts.detail.sheetDescription");

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="end"
        className={cn(CONTACT_CRM_SHEET_CLASS, CONTACT_SHEET_MOTION_CLASS)}
        overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className={contactSheetHeaderClass}>
          <div className="flex min-w-0 items-start gap-3">
            <span className={contactSheetHeaderIconClass} aria-hidden="true">
              {mode === "edit" ? <Pencil className="size-4" /> : <ContactRound className="size-4" />}
            </span>
            <div className="grid min-w-0 gap-1">
              <SheetTitle id={titleId} className={contactSheetTitleClass}>
                {title}
              </SheetTitle>
              <SheetDescription id={descriptionId} className={contactSheetDescriptionClass}>
                {description}
              </SheetDescription>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CloseIcon className="size-4" aria-hidden="true" />
          </Button>
        </header>
        <div className="flex min-h-0 flex-1 flex-col">
          <ContactDetailEditor
            detail={detail}
            loading={loading}
            groups={groups}
            organizationId={organizationId}
            mode={mode}
            onModeChange={setMode}
            onSave={onSave}
            onDelete={onDelete}
            onRestore={onRestore}
            onMerged={onMerged}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
